import { FC, useState, useCallback } from "react";
import { CorrectionBillEditBillInfoProps } from "../types/CorrectionBillEdit.types";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { DocumentDto } from "../../../../../../api-client";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import { BillingStepper } from "../../../../Base/components/Stepper";
import {
  A5PrintComponent,
  BillCardList,
  ExportConfirmationDialog,
  InputTestTypeDialog,
  TaxierenConfirmationDialog,
} from "../../../DirectBilling/Edit/exports";
import { TestType } from "../../../DirectBilling/Edit/types/TestTypes.interface";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import A4PrintComponent from "../../../DirectBilling/Edit/components/helper-components/A4PrintComponent";
import { useBillSummary } from "../../../Base/hooks/useBillSummary";
import BillSummary from "../../../DirectBilling/NewBill/components/sub-components/BillSummary";
import { Delete } from "@mui/icons-material";
import { CorrectionBillHeaderButtons } from "./CorrectionBillHeaderButtons";
import { useCorrectionBillEditBillInfo } from "../exports";

const CorrectionBillEditBillInfo: FC<CorrectionBillEditBillInfoProps> = (props) => {
  const {
    // states
    anchorEl,
    openDeleteDialog,
    bills,
    notes,
    openExportDialog,
    selectedRxId,
    openTaxierenDialog,
    printData,
    isPrinting,
    isDrucken,
    batchProgress,
    printRef,
    isExportLoading,
    todayString,
    isSoftDeleteDialogOpen,
    testToggleChecked,
    openTestTypeDialog,
    testTypeData,
    billsChanged,

    // handlers
    setOpenCancelTaxierenDialog,
    setOpenExportDialog,
    setSelectedRxId,
    setTestTypeData,
    setTestToggleChecked,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleTaxierenClick,
    handleCancelTaxierenConfirm,
    // handleAddSingleRx,
    handleValidationFilter,
    handleSave,
    handleDrucken,
    handleExport,
    handleExportConfirm,
    handleSoftDeleteCancel,
    handleSoftDeleteConfirm,
    handleTestToggleChange,
    handleTestTypeDialogClose,
    handleNotesChange,
    setIsDrucken,

    // from props
    onRowClick,
    onRemoveAll,
  } = useCorrectionBillEditBillInfo(props);

  const { totalRxs, passedValidationRxs, problematicRxs, taxiertRxs, numberOfPayers } =
    useBillSummary({ bills });

  const TransactionStore = useStore(TransactionsContext);
  const { uploadRxScanToExistingBill } = TransactionStore;

  const handleUploadRxScan = useCallback(
    async (billId: string, file: File): Promise<DocumentDto | undefined> => {
      const bill = bills.find((b) => b.billId === billId);
      const ownerId = bill?.payerIK || "";
      return uploadRxScanToExistingBill(billId, file, ownerId);
    },
    [bills, uploadRxScanToExistingBill],
  );

  // Track if printing has been completed
  const [hasBeenPrinted, setHasBeenPrinted] = useState(false);

  // Compute stepper logic
  const computeStep = () => {
    // Check if all bills have been exported
    const allBillsExported = bills.length > 0 && bills.every((bill) => bill.wasExported === true);

    // Check if all rxs have been taxiert (validated)
    const allRxsTaxiert =
      bills.length > 0 &&
      bills.every((bill) => bill.data.rxs.every((rx) => rx.isTaxieren === true));

    if (allBillsExported) return 4; // Final step: exported
    if (allRxsTaxiert && hasBeenPrinted) return 3; // Printed or printing
    if (allRxsTaxiert) return 2; // Taxiert but not printed
    if (billsChanged && allRxsTaxiert) return 1; // Changes made, but still taxiert
    return 0; // Initial state or changes made that need to be saved
  };

  const activeStep = computeStep();

  const handleSaveClicked = async () => {
    await handleSave();
    // activeStep will update automatically based on the new state
  };

  const handleDruckenClicked = async () => {
    setIsDrucken(true);
    try {
      await handleDrucken(testTypeData.type);
      setHasBeenPrinted(true); // Mark as printed for the stepper
    } finally {
      setIsDrucken(false);
    }
  };

  const handleExportClicked = async () => {
    await handleExport();
    // activeStep will update automatically based on the new state
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Backdrop for printing */}
      {/* Use override values if provided (for LineItem corrections), otherwise use hook values */}
      <WaitingBackdrop
        isWorking={
          props.isDruckenOverride !== undefined
            ? props.isDruckenOverride
            : isPrinting || isDrucken
        }
        message={
          (props.isDruckenOverride !== undefined ? props.isDruckenOverride : isDrucken)
            ? "PDF-Generierung läuft..."
            : undefined
        }
        progress={
          (props.isDruckenOverride !== undefined ? props.isDruckenOverride : isDrucken)
            ? props.batchProgress ?? batchProgress
            : undefined
        }
      />

      {/* Header Buttons */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 10px 10px",
        }}
      >
        <Box sx={{ mt: 1 }}>
          <BillingStepper
            activeStep={activeStep}
            isPaperBilling={false} // Use the 3-step paper billing process for corrections
          />
        </Box>
        <CorrectionBillHeaderButtons
          handleValidationFilter={handleValidationFilter}
          // Use stepper-aware handlers that update stepper state
          handleSave={handleSaveClicked}
          handleDrucken={handleDruckenClicked}
          handleExport={handleExportClicked}
          testToggleChecked={testToggleChecked}
          handleTestToggleChange={handleTestToggleChange}
          testTypeData={testTypeData}
        />
        <Box sx={{ px: 1 }}>
          <BillSummary
            totalRxs={totalRxs}
            passedValidationRxs={passedValidationRxs}
            problematicRxs={problematicRxs}
            taxiertRxs={taxiertRxs}
            numberOfPayers={numberOfPayers}
          />
        </Box>
      </Box>

      {/* BILLS */}
      <BillCardList
        bills={bills}
        todayString={todayString}
        selectedRxId={selectedRxId}
        notes={notes}
        setNotes={handleNotesChange}
        onRowClick={onRowClick}
        setSelectedRxId={setSelectedRxId}
        handleMenuOpen={handleMenuOpen}
        handleTaxierenClick={handleTaxierenClick}
        onRemoveAll={onRemoveAll}
        onUploadRxScan={handleUploadRxScan}
        isCorrection={true}
      />

      {/* Export Confirmation Dialog */}
      <ExportConfirmationDialog
        open={openExportDialog}
        onClose={() => {
          setOpenExportDialog(false);
        }}
        onConfirm={() => {
          if (testToggleChecked) {
            handleExportConfirm(testTypeData); // If user is in test mode, pass the testType, If not in test mode, pass nothing => defaults to GLOBAL
          } else {
            handleExportConfirm({ type: TestType.GLOBAL, email: undefined });
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {/* Soft Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={isSoftDeleteDialogOpen}
        onCancel={handleSoftDeleteCancel}
        onConfirm={handleSoftDeleteConfirm}
        title="Transaktionsbestätigung löschen"
        content="Dies ist das letzte Element. Möchten Sie die Transaktion wirklich löschen?"
      />

      {/* Taxieren Confirmation Dialog */}
      <TaxierenConfirmationDialog
        open={openTaxierenDialog}
        onClose={() => {
          setOpenCancelTaxierenDialog(false);
        }}
        onConfirm={handleCancelTaxierenConfirm}
      />

      {/* Print Component For Taxiren*/}
      {printData && (
        <div style={{ display: "none" }}>
          {printData.isZahnarzt ? (
            <A4PrintComponent
              ref={printRef}
              billNumber={printData.billNumber}
              rxNumber={printData.rxNumber}
              careProviderIk={printData.careProviderIk}
            />
          ) : (
            <A5PrintComponent
              ref={printRef}
              billNumber={printData.billNumber}
              rxNumber={printData.rxNumber}
              careProviderIk={printData.careProviderIk}
            />
          )}
        </div>
      )}

      {/* Test Type Dialog - For Test Mode Only */}
      <InputTestTypeDialog
        isOpen={openTestTypeDialog}
        onClose={() => {
          setTestToggleChecked(false);
          handleTestTypeDialogClose();
        }}
        onConfirm={(chosenType, chosenEmail) => {
          setTestTypeData({ type: chosenType, email: chosenEmail });
          handleTestTypeDialogClose();
        }}
      />

      {/* Backdrop for export */}
      {isExportLoading && (
        <WaitingBackdrop isWorking={isExportLoading} message="Bitte warten Sie. Exportieren... " />
      )}

      {/* Kebab Menu (Actions) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick}>
          {" "}
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Entfernen</ListItemText>
        </MenuItem>
        {/* <MenuItem onClick={handleCancelTaxierenClick}><ListItemIcon><ApprovalRounded/></ListItemIcon><ListItemText>Untaxieren</ListItemText></MenuItem> */}
      </Menu>
    </Box>
  );
};

export default CorrectionBillEditBillInfo;
